import type { Rarity } from "./rarity.js";

export const ACADEMY_MAX_LEVEL = 10;
export const ACADEMY_MVP_INITIAL_LEVEL = ACADEMY_MAX_LEVEL;
export const ACADEMY_RECRUITMENT_TABLE_VERSION = "academy-levels-v1";
export const SCROLL_SUMMON_TABLE_VERSION = "academy-scrolls-v1";
export const RECRUITMENT_ROLL_RANGE = 10_000;

export type RecruitmentRarity = Exclude<Rarity, "mythic">;
export type SummoningScrollRarity = Exclude<RecruitmentRarity, "common">;

export interface AcademyLevelDefinition {
  readonly level: number;
  readonly recruitmentIntervalMs: number;
  readonly rarityChances: Readonly<Record<RecruitmentRarity, number>>;
}

export interface SummoningScrollDefinition {
  readonly itemId: string;
  readonly rarity: SummoningScrollRarity;
  readonly rarityChances: Readonly<Record<RecruitmentRarity, number>>;
}

const hours = (value: number): number => value * 60 * 60 * 1000;
const minutes = (value: number): number => value * 60 * 1000;

/** Canonical recruitment effects, keyed by level so UI and server share one row. */
export const ACADEMY_LEVELS = [
  {
    level: 1,
    recruitmentIntervalMs: hours(8),
    rarityChances: {
      common: 9_500,
      uncommon: 500,
      rare: 0,
      epic: 0,
      legendary: 0,
    },
  },
  {
    level: 2,
    recruitmentIntervalMs: hours(7) + minutes(30),
    rarityChances: {
      common: 9_200,
      uncommon: 800,
      rare: 0,
      epic: 0,
      legendary: 0,
    },
  },
  {
    level: 3,
    recruitmentIntervalMs: hours(7),
    rarityChances: {
      common: 8_500,
      uncommon: 1_300,
      rare: 200,
      epic: 0,
      legendary: 0,
    },
  },
  {
    level: 4,
    recruitmentIntervalMs: hours(6) + minutes(30),
    rarityChances: {
      common: 7_800,
      uncommon: 1_800,
      rare: 400,
      epic: 0,
      legendary: 0,
    },
  },
  {
    level: 5,
    recruitmentIntervalMs: hours(6),
    rarityChances: {
      common: 7_000,
      uncommon: 2_200,
      rare: 700,
      epic: 100,
      legendary: 0,
    },
  },
  {
    level: 6,
    recruitmentIntervalMs: hours(5) + minutes(40),
    rarityChances: {
      common: 6_400,
      uncommon: 2_500,
      rare: 900,
      epic: 200,
      legendary: 0,
    },
  },
  {
    level: 7,
    recruitmentIntervalMs: hours(5) + minutes(20),
    rarityChances: {
      common: 5_700,
      uncommon: 2_900,
      rare: 1_100,
      epic: 300,
      legendary: 0,
    },
  },
  {
    level: 8,
    recruitmentIntervalMs: hours(5),
    rarityChances: {
      common: 5_000,
      uncommon: 3_000,
      rare: 1_500,
      epic: 400,
      legendary: 100,
    },
  },
  {
    level: 9,
    recruitmentIntervalMs: hours(4) + minutes(30),
    rarityChances: {
      common: 4_200,
      uncommon: 3_500,
      rare: 1_800,
      epic: 400,
      legendary: 100,
    },
  },
  {
    level: 10,
    recruitmentIntervalMs: hours(4),
    rarityChances: {
      common: 3_500,
      uncommon: 4_000,
      rare: 2_000,
      epic: 400,
      legendary: 100,
    },
  },
] as const satisfies readonly AcademyLevelDefinition[];

/**
 * Summoning scroll profiles are versioned prototype balance values. Rare/Epic
 * retain the design's minimum-rarity guarantee; Mythic remains out of these
 * initial profiles. A scroll is consumed when this result is locked.
 */
export const SUMMONING_SCROLLS = [
  {
    itemId: "academy_summoning_scroll_uncommon",
    rarity: "uncommon",
    rarityChances: {
      common: 0,
      uncommon: 8_500,
      rare: 1_300,
      epic: 200,
      legendary: 0,
    },
  },
  {
    itemId: "academy_summoning_scroll_rare",
    rarity: "rare",
    rarityChances: {
      common: 0,
      uncommon: 0,
      rare: 8_500,
      epic: 1_300,
      legendary: 200,
    },
  },
  {
    itemId: "academy_summoning_scroll_epic",
    rarity: "epic",
    rarityChances: {
      common: 0,
      uncommon: 0,
      rare: 0,
      epic: 9_000,
      legendary: 1_000,
    },
  },
  {
    itemId: "academy_summoning_scroll_legendary",
    rarity: "legendary",
    rarityChances: {
      common: 0,
      uncommon: 0,
      rare: 0,
      epic: 0,
      legendary: 10_000,
    },
  },
] as const satisfies readonly SummoningScrollDefinition[];

function assertChanceTable(
  chances: Readonly<Record<RecruitmentRarity, number>>,
  label: string,
): void {
  const entries = Object.values(chances);
  if (
    entries.some((chance) => !Number.isInteger(chance) || chance < 0) ||
    entries.reduce((sum, chance) => sum + chance, 0) !== RECRUITMENT_ROLL_RANGE
  )
    throw new Error(
      `${label} rarity chances must total ${RECRUITMENT_ROLL_RANGE}`,
    );
}

for (const definition of ACADEMY_LEVELS)
  assertChanceTable(
    definition.rarityChances,
    `Academy level ${definition.level}`,
  );
for (const definition of SUMMONING_SCROLLS)
  assertChanceTable(definition.rarityChances, definition.itemId);

export function academyLevelDefinition(level: number): AcademyLevelDefinition {
  if (!Number.isInteger(level) || level < 1 || level > ACADEMY_MAX_LEVEL)
    throw new RangeError(
      `academyLevel must be an integer from 1 to ${ACADEMY_MAX_LEVEL}`,
    );
  return ACADEMY_LEVELS[level - 1]!;
}

export function summoningScrollDefinition(
  itemId: string,
): SummoningScrollDefinition | undefined {
  return SUMMONING_SCROLLS.find((definition) => definition.itemId === itemId);
}

export function rollRecruitmentRarity(
  chances: Readonly<Record<RecruitmentRarity, number>>,
  randomRoll: () => number,
): RecruitmentRarity {
  assertChanceTable(chances, "Recruitment");
  const roll = randomRoll();
  if (!Number.isInteger(roll) || roll < 0 || roll >= RECRUITMENT_ROLL_RANGE)
    throw new RangeError(
      `Recruitment roll must be from 0 to ${RECRUITMENT_ROLL_RANGE - 1}`,
    );
  let cumulative = 0;
  for (const rarity of [
    "common",
    "uncommon",
    "rare",
    "epic",
    "legendary",
  ] as const) {
    cumulative += chances[rarity];
    if (roll < cumulative) return rarity;
  }
  throw new Error("Recruitment chance table did not resolve a rarity");
}

export function rollAcademyRarity(
  level: number,
  randomRoll: () => number,
): RecruitmentRarity {
  return rollRecruitmentRarity(
    academyLevelDefinition(level).rarityChances,
    randomRoll,
  );
}

export function rollScrollRarity(
  itemId: string,
  randomRoll: () => number,
): RecruitmentRarity {
  const definition = summoningScrollDefinition(itemId);
  if (!definition) throw new RangeError(`Unknown summoning scroll: ${itemId}`);
  return rollRecruitmentRarity(definition.rarityChances, randomRoll);
}
